import React, { useMemo, useState } from 'react';
import {
  Search,
  Plus,
  CalendarDays,
  Wallet,
  TrendingUp,
  Menu,
} from 'lucide-react';
import { User, Customer, Booking } from '../types';
import { api } from '../services/api';
import Sidebar from './Sidebar';
import CustomerList from './CustomerList';
import CustomerDetail from './CustomerDetail';
import CommunicationHistory from './CommunicationHistory';
import BookingListView from './BookingListView';
import CalendarView from './CalendarView';
import AddBookingModal from './AddBookingModal';
import AddCustomerModal from './AddCustomerModal';
import { Button, IconButton, Input, StatCard, useToast } from './ui';

interface DashboardProps {
  user: User;
  customers: Customer[];
  onLogout: () => void;
  setCustomers: React.Dispatch<React.SetStateAction<Customer[]>>;
  refreshData: () => Promise<void>;
}

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(n);

const Dashboard: React.FC<DashboardProps> = ({
  user,
  customers,
  onLogout,
  refreshData,
}) => {
  const [activeTab, setActiveTab] = useState<
    'crm' | 'contacts' | 'bookings' | 'calendar'
  >('crm');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const [isAddBookingOpen, setIsAddBookingOpen] = useState(false);
  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);

  const toast = useToast();
  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId);

  const stats = useMemo(() => {
    let grossIncome = 0;
    let netIncome = 0;
    let totalBookings = 0;
    customers.forEach((customer) => {
      customer.bookings?.forEach((b) => {
        grossIncome += Number(b.amountPaid || 0);
        netIncome += Number(b.ourIncome || 0);
        totalBookings++;
      });
    });
    return { grossIncome, netIncome, totalBookings };
  }, [customers]);

  const filteredCustomers = customers.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.phone || '').includes(searchTerm)
  );

  const handleCustomerSubmit = async (customer: Customer) => {
    try {
      const { bookings: _bookings, contacts: _contacts, ...payload } =
        customer;
      const isNew = payload.id?.startsWith('c');
      if (isNew) delete (payload as Partial<Customer>).id;
      await api.saveCustomer(payload);
      await refreshData();
      setIsAddCustomerOpen(false);
      setEditingCustomer(null);
      toast.show(
        isNew ? 'Customer added.' : 'Customer updated.',
        'success'
      );
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to save customer';
      toast.show(message, 'error');
    }
  };

  const handleDeleteCustomer = async (id: string) => {
    const customer = customers.find((c) => c.id === id);
    const confirmed = window.confirm(
      `Delete ${customer?.name ?? 'this customer'}? This removes their bookings and history.`
    );
    if (!confirmed) return;
    try {
      await api.deleteCustomer(id);
      if (selectedCustomerId === id) setSelectedCustomerId(null);
      await refreshData();
      toast.show('Customer deleted.', 'success');
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to delete customer';
      toast.show(message, 'error');
    }
  };

  const handleBookingSubmit = async (booking: Booking) => {
    try {
      await api.saveBooking(booking);
      await refreshData();
      setIsAddBookingOpen(false);
      setEditingBooking(null);
      toast.show(
        booking.id ? 'Booking updated.' : 'Booking added.',
        'success'
      );
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to save booking';
      toast.show(message, 'error');
    }
  };

  const handleDeleteBooking = async (id: string) => {
    if (!window.confirm('Delete this booking record?')) return;
    try {
      await api.deleteBooking(id);
      await refreshData();
      toast.show('Booking deleted.', 'success');
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to delete booking';
      toast.show(message, 'error');
    }
  };

  const openEditCustomer = (customer: Customer) => {
    setEditingCustomer(customer);
    setIsAddCustomerOpen(true);
  };

  const openEditBooking = (booking: Booking) => {
    setEditingBooking(booking);
    setIsAddBookingOpen(true);
  };

  const searchPlaceholder =
    activeTab === 'crm'
      ? 'Search customers…'
      : activeTab === 'bookings'
      ? 'Search bookings…'
      : activeTab === 'contacts'
      ? 'Search messages…'
      : 'Search…';

  return (
    <div className="flex h-[100dvh] bg-sand-50 overflow-hidden">
      <Sidebar
        activeTab={activeTab}
        setActiveTab={(tab) => {
          setActiveTab(tab);
          setSelectedCustomerId(null);
        }}
        user={user}
        onLogout={onLogout}
        isMobileOpen={mobileNavOpen}
        onCloseMobile={() => setMobileNavOpen(false)}
      />

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-14 md:h-16 bg-white border-b border-sand-200 flex items-center gap-2 px-3 md:px-6 shrink-0">
          <IconButton
            label="Open menu"
            onClick={() => setMobileNavOpen(true)}
            className="md:hidden"
          >
            <Menu className="h-5 w-5" />
          </IconButton>

          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sand-400" />
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
                className="pl-9 bg-sand-50 border-sand-200"
              />
            </div>
          </div>

          <div className="flex items-center gap-1.5 md:gap-2">
            <Button
              variant="secondary"
              leftIcon={<CalendarDays className="h-4 w-4" />}
              onClick={() => setActiveTab('calendar')}
              className="hidden lg:inline-flex"
            >
              Calendar
            </Button>

            <Button
              variant="secondary"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => {
                setEditingCustomer(null);
                setIsAddCustomerOpen(true);
              }}
              className="hidden md:inline-flex"
            >
              Customer
            </Button>
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => {
                setEditingBooking(null);
                setIsAddBookingOpen(true);
              }}
              className="hidden md:inline-flex"
            >
              Booking
            </Button>

            <Button
              size="sm"
              onClick={() => {
                setEditingBooking(null);
                setIsAddBookingOpen(true);
              }}
              className="md:hidden h-9 w-9 px-0"
              aria-label="Add booking"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="px-3 md:px-6 pt-4 md:pt-6 grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-3 shrink-0">
            <StatCard
              label="Net income"
              value={formatCurrency(stats.netIncome)}
              tone="sage"
              icon={<TrendingUp className="h-5 w-5" />}
            />
            <StatCard
              label="Total bookings"
              value={stats.totalBookings.toLocaleString('en-GB')}
              tone="accent"
              icon={<CalendarDays className="h-5 w-5" />}
            />
            {/* Gross income — hidden on mobile to save vertical space */}
            <div className="hidden sm:block">
              <StatCard
                label="Gross income"
                value={formatCurrency(stats.grossIncome)}
                icon={<Wallet className="h-5 w-5" />}
              />
            </div>
          </div>

          <div
            className={[
              'flex-1 overflow-y-auto transition-all duration-300 relative',
              selectedCustomerId ? 'md:mr-[24rem]' : '',
            ].join(' ')}
          >
            {activeTab === 'crm' && (
              <CustomerList
                customers={filteredCustomers}
                onSelectCustomer={setSelectedCustomerId}
                onEditCustomer={openEditCustomer}
                onDeleteCustomer={handleDeleteCustomer}
                selectedCustomerId={selectedCustomerId}
              />
            )}
            {activeTab === 'bookings' && (
              <BookingListView
                customers={customers}
                searchTerm={searchTerm}
                onEditBooking={openEditBooking}
                onDeleteBooking={handleDeleteBooking}
              />
            )}
            {activeTab === 'calendar' && (
              <CalendarView
                customers={customers}
                onEditBooking={openEditBooking}
              />
            )}
            {activeTab === 'contacts' && (
              <CommunicationHistory customers={customers} />
            )}
          </div>

          {selectedCustomer && (
            <div
              className={[
                'fixed z-40 bg-white shadow-modal animate-slide-in-right',
                'inset-0 top-14',
                'md:inset-x-auto md:top-16 md:right-0 md:bottom-0 md:w-96 md:border-l md:border-sand-200',
              ].join(' ')}
            >
              <CustomerDetail
                customer={selectedCustomer}
                onClose={() => setSelectedCustomerId(null)}
                onEditBooking={openEditBooking}
                onDeleteBooking={handleDeleteBooking}
              />
            </div>
          )}
        </div>
      </main>

      <AddBookingModal
        open={isAddBookingOpen}
        customers={customers}
        onClose={() => {
          setIsAddBookingOpen(false);
          setEditingBooking(null);
        }}
        onSubmit={handleBookingSubmit}
        initialData={editingBooking}
      />
      <AddCustomerModal
        open={isAddCustomerOpen}
        onClose={() => {
          setIsAddCustomerOpen(false);
          setEditingCustomer(null);
        }}
        onSubmit={handleCustomerSubmit}
        initialData={editingCustomer}
      />
    </div>
  );
};

export default Dashboard;
